import { DataWithCount, Round } from './../../types'
import { basePath, callAPI, callAPIRaw, callAuthAPI } from './common'
import { FundingRoundData } from '../../types/api'
import { NextPageContext } from 'next'
import { printError } from '../log'

export type Sort =
  | 'startDate'
  | 'endDate'
  | 'amount'
  | 'preValue'
  | 'postValue'
  | 'date'
  | 'preMoney'
  | 'postMoney'
  | null

export type Order = 'desc' | 'asc' | null

export type SearchConditions = {
  keywords: string
  tags?: string
  initialSeries: string
  fundingAmountFrom?: number
  fundingAmountTo?: number
  preValuationFrom?: number
  preValuationTo?: number
  postValuationFrom?: number
  postValuationTo?: number
  termFrom?: string | null
  termTo?: string | null
  page?: number
  limit?: number
  offset?: number
  sort?: Sort
  order?: Order
}

const delay = (
  signal: AbortSignal,
  duration: number = 1500
): Promise<boolean> => {
  return new Promise((resolve) => {
    signal.addEventListener('abort', () => {
      resolve(true)
    })
    setTimeout(() => {
      resolve(false)
    }, duration)
  })
}

export const ABORTED = Symbol('aborted')

type TargetFunction<T> = (
  conditions?: SearchConditions,
  options?: RequestInit
) => Promise<DataWithCount<T[]>>

const makeDebounced = <T>(target: TargetFunction<T>) => {
  let ctrl: AbortController | undefined
  return async (conditions?: SearchConditions, options?: RequestInit) => {
    if (ctrl) {
      ctrl.abort()
    }

    ctrl = new AbortController()
    const aborted = await delay(ctrl.signal)
    if (aborted) {
      throw ABORTED
    }

    ctrl = new AbortController()
    let ret
    try {
      ret = await target(conditions, { ...options, signal: ctrl.signal })
    } catch (e: unknown) {
      if ((e as any).name === 'AbortError') {
        throw ABORTED
      } else {
        throw e
      }
    }

    ctrl = undefined
    return ret
  }
}

export const getRounds = async (
  conditions?: SearchConditions,
  options?: RequestInit
) => {
  const query = conditions ? buildQuery(conditions) : ''
  const res = await callAPI<DataWithCount<Round[]>>(
    `${basePath}/rounds/?${query}`,
    options
  )
  return res
}

export const getRoundsDebounced = makeDebounced(getRounds)

export const getFundingRoundsSSR = async (
  ctx: NextPageContext,
  conditions?: SearchConditions
) => {
  let res
  try {
    const query = conditions ? buildQuery(conditions) : ''
    res = await callAuthAPI<DataWithCount<FundingRoundData[]>>(
      ctx,
      `${basePath}/fundraise_rounds/?${query}`
    )
  } catch (e) {
    printError(e)
  }
  return res ? res : { data: [], count: 0 }
}

export const getFundingRounds = async (
  conditions?: SearchConditions,
  options?: RequestInit
) => {
  const query = conditions ? buildQuery(conditions) : ''
  const res = await callAPI<DataWithCount<FundingRoundData[]>>(
    `${basePath}/fundraise_rounds/?${query}`,
    options
  )
  return res
}

export const getFundingRoundsDebounced = makeDebounced(getFundingRounds)

const buildQuery = (conditions: SearchConditions) => {
  return Object.entries(conditions)
    .filter((condition) => {
      const value = condition[1]
      return value
    })
    .map((condition) => condition.join('='))
    .join('&')
}

declare global {
  interface Navigator {
    msSaveBlob?: (blob: any, defaultName?: string) => boolean
  }
}

export const downloadReport = async (
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  searchConditions: SearchConditions
) => {
  try {
    setIsLoading(true)
    const query = searchConditions ? buildQuery(searchConditions) : ''
    const res = await callAPIRaw(
      `${basePath}/fundraise_rounds/report?${query}`,
      {
        method: 'POST',
        credentials: 'include',
      }
    )
    const blob = await res.blob()
    const anchor = document.createElement('a')
    const url = URL.createObjectURL(blob)
    const encodedFileName = res.headers
      .get('Content-Disposition')
      ?.split('filename=')?.[1]
      ?.replace(/['"]+/g, '')

    if (!!encodedFileName) {
      const fileName = decodeURIComponent(encodedFileName)

      if (navigator.userAgent.includes('Trident/') && navigator.msSaveBlob) {
        navigator.msSaveBlob(blob, fileName)
        return
      }

      try {
        anchor.href = url
        anchor.download = fileName
        anchor.hidden = true
        document.body.append(anchor)
        anchor.click()
      } finally {
        setTimeout(() => {
          anchor.remove()
          URL.revokeObjectURL(url)
        }, 300)
      }
    }
  } catch (e) {
    throw e
  } finally {
    setIsLoading(false)
  }
}
